import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/useAuth";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { ApiError } from "@/services/api/errors";
import {
  type DevToolsDiagnostics,
  getDevToolsDiagnostics,
  resetOnboardingForTesting,
} from "@/services/api/dev-tools-client";
import { getAllFlags } from "@/services/feature-flags";

const fieldRowClass =
  "flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--border)] py-2 text-sm last:border-b-0";

/** Flat "key: value" text block — kept in sync with the diagnostics panel below. */
function buildDebugInfoText(fields: {
  userId: string | undefined;
  firebaseUid: string | undefined;
  email: string | undefined | null;
  diagnostics: DevToolsDiagnostics | null;
  flags: Record<string, boolean>;
}): string {
  const lines = [
    `userId: ${fields.userId ?? ""}`,
    `firebaseUid: ${fields.firebaseUid ?? ""}`,
    `email: ${fields.email ?? ""}`,
    `gitSha: ${fields.diagnostics?.gitSha ?? ""}`,
    `nodeEnv: ${fields.diagnostics?.nodeEnv ?? ""}`,
  ];
  for (const [flag, enabled] of Object.entries(fields.flags)) {
    lines.push(`flag.${flag}: ${enabled}`);
  }
  return lines.join("\n");
}

/**
 * Settings → Testing tools: internal-tester-only self-service helpers
 * (docs/specs/settings-internal-testing-tools.md). Force onboarding + a
 * read-only diagnostics/copy-debug-info panel. Rendered only when the
 * caller's profile carries `isInternalTester` (server-decided, never a
 * client-side check) — see Settings.tsx.
 */
export default function SettingsTestingSection() {
  const { t } = useTranslation(["settings", "common"]);
  const { user, profile, refreshProfile } = useAuth();

  const [clearCompanyData, setClearCompanyData] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetStatus, setResetStatus] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const [diagnostics, setDiagnostics] = useState<DevToolsDiagnostics | null>(
    null,
  );
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(
    null,
  );
  const { isCopied, isError: isCopyError, copy } = useCopyToClipboard();

  const flags = getAllFlags();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await getDevToolsDiagnostics();
        if (!cancelled) setDiagnostics(result);
      } catch {
        if (!cancelled) {
          setDiagnosticsError(t("settings:testing.diagnosticsLoadError"));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleForceOnboarding = useCallback(async () => {
    setResetStatus(null);
    setResetError(null);
    setResetting(true);
    try {
      await resetOnboardingForTesting(clearCompanyData);
      setResetStatus(t("settings:testing.forceOnboardingSuccess"));
      // useOnboardingGuard redirects to /onboarding once the refreshed
      // profile shows onboardingCompleted: false.
      await refreshProfile();
    } catch (err) {
      const message = err instanceof ApiError && err.status === 429
        ? t("settings:errors.rateLimited")
        : t("settings:testing.forceOnboardingError");
      setResetError(message);
    } finally {
      setResetting(false);
    }
  }, [clearCompanyData, refreshProfile, t]);

  const handleCopyDebugInfo = useCallback(() => {
    const text = buildDebugInfoText({
      userId: profile?.id,
      firebaseUid: user?.uid,
      email: profile?.email ?? user?.email,
      diagnostics,
      flags,
    });
    void copy(text);
  }, [copy, diagnostics, flags, profile, user]);

  return (
    <div className="space-y-6">
      <section
        className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6"
        data-test="settings-testing-force-onboarding"
      >
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            {t("settings:testing.forceOnboardingTitle")}
          </h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {t("settings:testing.forceOnboardingHint")}
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm text-[var(--foreground)]">
          <Checkbox
            checked={clearCompanyData}
            onCheckedChange={(value) => setClearCompanyData(value === true)}
            data-test="settings-testing-clear-company-data"
          />
          {t("settings:testing.clearCompanyDataLabel")}
        </label>

        {resetStatus && (
          <Alert data-test="settings-testing-reset-status" variant="success">
            <AlertDescription>{resetStatus}</AlertDescription>
          </Alert>
        )}
        {resetError && (
          <Alert data-test="settings-testing-reset-error" variant="error">
            <AlertDescription>{resetError}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            variant="cta"
            onClick={handleForceOnboarding}
            disabled={resetting}
            data-test="settings-testing-force-onboarding-submit"
          >
            {t("settings:testing.forceOnboardingButton")}
          </Button>
        </div>
      </section>

      <section
        className="space-y-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-6"
        data-test="settings-testing-diagnostics"
      >
        <div>
          <h2 className="text-lg font-semibold text-[var(--foreground)]">
            {t("settings:testing.diagnosticsTitle")}
          </h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {t("settings:testing.diagnosticsHint")}
          </p>
        </div>

        {diagnosticsError && (
          <Alert data-test="settings-testing-diagnostics-error" variant="error">
            <AlertDescription>{diagnosticsError}</AlertDescription>
          </Alert>
        )}

        <dl className="text-sm">
          <div className={fieldRowClass}>
            <dt className="text-[var(--muted-foreground)]">
              {t("settings:testing.userIdLabel")}
            </dt>
            <dd data-test="settings-testing-user-id">{profile?.id}</dd>
          </div>
          <div className={fieldRowClass}>
            <dt className="text-[var(--muted-foreground)]">
              {t("settings:testing.firebaseUidLabel")}
            </dt>
            <dd data-test="settings-testing-firebase-uid">{user?.uid}</dd>
          </div>
          <div className={fieldRowClass}>
            <dt className="text-[var(--muted-foreground)]">
              {t("settings:testing.emailLabel")}
            </dt>
            <dd data-test="settings-testing-email">
              {profile?.email ?? user?.email}
            </dd>
          </div>
          <div className={fieldRowClass}>
            <dt className="text-[var(--muted-foreground)]">
              {t("settings:testing.gitShaLabel")}
            </dt>
            <dd data-test="settings-testing-git-sha">{diagnostics?.gitSha}</dd>
          </div>
          <div className={fieldRowClass}>
            <dt className="text-[var(--muted-foreground)]">
              {t("settings:testing.nodeEnvLabel")}
            </dt>
            <dd data-test="settings-testing-node-env">{diagnostics?.nodeEnv}</dd>
          </div>
        </dl>

        <div>
          <h3 className="text-sm font-medium text-[var(--foreground)]">
            {t("settings:testing.featureFlagsLabel")}
          </h3>
          <dl className="mt-1 text-sm" data-test="settings-testing-feature-flags">
            {Object.entries(flags).map(([flag, enabled]) => (
              <div key={flag} className={fieldRowClass}>
                <dt className="text-[var(--muted-foreground)]">{flag}</dt>
                <dd>{String(enabled)}</dd>
              </div>
            ))}
          </dl>
        </div>

        {isCopied && (
          <Alert data-test="settings-testing-copy-status" variant="success">
            <AlertDescription>{t("settings:testing.copyDebugInfoSuccess")}</AlertDescription>
          </Alert>
        )}
        {isCopyError && (
          <Alert data-test="settings-testing-copy-error" variant="error">
            <AlertDescription>{t("settings:testing.copyDebugInfoError")}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={handleCopyDebugInfo}
            data-test="settings-testing-copy-debug-info"
          >
            {t("settings:testing.copyDebugInfoButton")}
          </Button>
        </div>
      </section>
    </div>
  );
}
